import React, { useRef, useState, useEffect } from 'react';
import { saveAutomation } from '../../../services/AutomationsService';
import { getDefaultIndexes } from '../../../services/BeholderService';
import { buildAutomationIndexesForSymbol } from '../../UnifiedSetup/automationSetupUtils';
import '../Automations.css';
import '../../../components/PrivatePage/private-page.css';
import LogButton from '../../../components/Logs/LogButton';
import { getSymbol } from '../../../services/SymbolsService';
import { Icon } from '@iconify/react';
import AutomationFormFields from './AutomationFormFields';
import ScheduledBotForm from '../ScheduledBot/ScheduledBotForm';

/**
 * props:
 * - data
 * - onSubmit
 * - inline (boolean)
 */
function AutomationModal(props) {
    const [indexes, setIndexes] = useState([]);
    const [error, setError] = useState('');
    const [symbol, setSymbol] = useState({});
    const [showLogs, setShowLogs] = useState(false);

    const DEFAULT_AUTOMATION = {
        id: 0,
        symbol: '',
        conditions: '',
        schedule: '',
        actions: [],
        maxOpenPositions: 0,
    };

    const [automation, setAutomation] = useState(DEFAULT_AUTOMATION);

    const btnClose = useRef('');
    const btnSave = useRef('');

    useEffect(() => {
        if (props.inline) return;

        const modal = document.getElementById('modalAutomation');
        if (!modal) return;

        modal.addEventListener('hidden.bs.modal', () => {
            setAutomation({ ...DEFAULT_AUTOMATION });
            setShowLogs(false);
        });
    }, [props.inline]);

    function onSubmit() {
        saveAutomation(automation.id, automation)
            .then((result) => {
                if (props.inline) {
                    if (props.onSubmit) props.onSubmit(result);
                } else {
                    btnClose.current.click();
                    if (props.onSubmit) props.onSubmit(result);
                }
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onInputChange(event) {
        const { id, value, type, checked } = event.target;
        let nextValue = id === 'maxOpenPositions'
            ? Math.max(0, parseInt(value, 10) || 0)
            : (type === 'checkbox' ? checked : value);
        if (id === 'symbol' && typeof nextValue === 'string') {
            nextValue = nextValue.toUpperCase();
        }
        setAutomation((prevState) => ({ ...prevState, [id]: nextValue }));
    }

    useEffect(() => {
        if (!props.data) return;
        setAutomation({
            ...DEFAULT_AUTOMATION,
            ...props.data,
            maxOpenPositions: props.data.maxOpenPositions || 0,
        });
    }, [props.data]);

    useEffect(() => {
        if (!automation || !automation.symbol) return;

        const automationSymbol = automation.symbol.toUpperCase();

        if (automationSymbol.startsWith('*')) {
            setSymbol({ base: '*', quote: automationSymbol.replace('*', '') });
        } else {
            getSymbol(automationSymbol)
                .then((symbolObj) => {
                    if (symbolObj) setSymbol(symbolObj);
                    else setIndexes(getDefaultIndexes(automationSymbol));
                })
                .catch((err) => {
                    console.error(err.response ? err.response.data : err.message);
                    setError(err.response ? err.response.data : err.message);
                });
        }
    }, [automation.symbol]);

    useEffect(() => {
        if (!automation?.symbol) {
            setIndexes([]);
            return;
        }

        let cancelled = false;
        buildAutomationIndexesForSymbol({
            setupSymbol: automation.symbol,
            symbolMeta: symbol,
        })
            .then((filteredIndexes) => {
                if (!cancelled) setIndexes(filteredIndexes);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(err.response ? err.response.data : err.message);
                setIndexes(getDefaultIndexes(automation.symbol.toUpperCase()));
                setError(err.response ? err.response.data : err.message);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol, automation.symbol]);

    function onLogClick() {
        setShowLogs(!showLogs);
    }

    const formFields = (
        <AutomationFormFields
            automation={automation}
            indexes={indexes}
            showLogs={showLogs}
            onInputChange={onInputChange}
            namePlaceholder={automation.schedule ? 'My scheduled bot name' : 'My automation name'}
        />
    );

    if (props.inline) {
        return (
            <ScheduledBotForm
                automation={automation}
                indexes={indexes}
                showLogs={showLogs}
                error={error}
                onInputChange={onInputChange}
                onLogClick={onLogClick}
                onSubmit={onSubmit}
            />
        );
    }

    return (
        <div className="modal fade" id="modalAutomation" tabIndex="-1" role="dialog" aria-labelledby="modalTitleNotify" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">
                            {props.data.id ? 'Edit ' : 'New '}
                            {props.data.schedule ? 'Scheduled ' : ''}
                            Automation
                        </p>
                        <button
                            ref={btnClose}
                            type="button"
                            className="text-gray-900 hover:border-[2px] rounded-md hover:border-gray-400 text-[1.5vw]"
                            data-bs-dismiss="modal"
                            aria-label="close"
                        >
                            <Icon icon="iconoir:cancel" className="text-[2vw]" />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">{formFields}</div>
                    </div>
                    <div className="modal-footer">
                        {error ? (
                            <div className="alert alert-danger mt-1 col-9 py-1">{error}</div>
                        ) : null}
                        <LogButton id={automation.id} onClick={onLogClick} />
                        <button
                            ref={btnSave}
                            type="button"
                            className="btn btn-sm border-[1px] bg-[#082272] hover:bg-[#082272f1] text-white"
                            onClick={onSubmit}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AutomationModal;